import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Clock, CheckCircle, AlertCircle, Filter } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: MessageSquare },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-500/20 text-green-300 border-green-500/30', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30', icon: CheckCircle }
};

const CATEGORY_CONFIG = {
  billing: { label: 'Billing', color: 'text-yellow-400' },
  uploading: { label: 'Uploading', color: 'text-green-400' },
  posting: { label: 'Posting', color: 'text-purple-400' },
  self_marketing: { label: 'Self-Marketing', color: 'text-orange-400' },
  technical: { label: 'Technical', color: 'text-red-400' },
  other: { label: 'Other', color: 'text-gray-400' }
};

export default function SupportTickets() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    loadTickets();
  }, [statusFilter, categoryFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const response = await axios.get(
        `${API_URL}/api/support/tickets?${params.toString()}`,
        { withCredentials: true }
      );
      setTickets(response.data);
    } catch (err) {
      console.error('Error loading tickets:', err);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const getCategoryConfig = (category) => CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Support Tickets</h1>
        <p className="text-gray-400">View and manage your support requests</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-gray-400 text-sm">Filter by:</span>
        </div>
        
        {/* Status Filter */}
        <select
          value={statusFilter || ''}
          onChange={(e) => setStatusFilter(e.target.value || null)}
          className="px-4 py-2 bg-[#1a1d23] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>

        {/* Category Filter */}
        <select
          value={categoryFilter || ''}
          onChange={(e) => setCategoryFilter(e.target.value || null)}
          className="px-4 py-2 bg-[#1a1d23] border border-gray-800 rounded-lg text-white focus:outline-none focus:border-cyan-600"
        >
          <option value="">All Categories</option>
          <option value="billing">Billing</option>
          <option value="uploading">Uploading</option>
          <option value="posting">Posting</option>
          <option value="self_marketing">Self-Marketing</option>
          <option value="technical">Technical</option>
          <option value="other">Other</option>
        </select>

        <button
          onClick={() => navigate('/contact-support')}
          className="ml-auto px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          New Ticket
        </button>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      ) : tickets.length > 0 ? (
        <div className="space-y-4">
          {tickets.map((ticket) => {
            const statusConfig = getStatusConfig(ticket.status);
            const categoryConfig = getCategoryConfig(ticket.category);
            const StatusIcon = statusConfig.icon;
            
            return (
              <button
                key={ticket.id}
                onClick={() => navigate(`/support/tickets/${ticket.id}`)}
                className="w-full p-6 bg-[#1a1d23] border border-gray-800 rounded-lg hover:border-cyan-600 transition-all text-left"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">{ticket.subject}</h3>
                    <div className="flex items-center gap-3 text-sm">
                      <span className={`px-3 py-1 rounded-full border ${statusConfig.color} inline-flex items-center gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                      <span className={`font-medium ${categoryConfig.color}`}>
                        {categoryConfig.label}
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {ticket.unread_count > 0 && (
                    <span className="px-2 py-1 bg-cyan-500 text-white text-xs rounded-full">
                      {ticket.unread_count} new
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No support tickets</h3>
          <p className="text-gray-400 mb-6">You haven't created any support tickets yet</p>
          <button
            onClick={() => navigate('/contact-support')}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
          >
            Create Your First Ticket
          </button>
        </div>
      )}
    </div>
  );
}
